// =============================================================================
// Empty State Utils - Shared logic for determining empty state reason
// Used by Index.tsx and ClickUpSidebar for consistent behavior
// =============================================================================

export type EmptyStateReason = 'no-content' | 'spaces-but-no-projects' | null;

interface Project {
  id: string;
  status?: string;
}

interface Space {
  id: string;
}

interface GetEmptyReasonParams {
  spaces: Space[];
  projects: Project[];
  canManage: boolean;
}

/**
 * Determines the reason for showing an empty state, or null if no empty state should be shown.
 * 
 * Logic (Option B):
 * - If user canManage (owner/admin): never show empty state (they see everything)
 * - If activeProjects.length > 0: no empty state
 * - If spaces.length > 0 but no active projects: 'spaces-but-no-projects'
 * - If no spaces and no active projects: 'no-content'
 */
export function getEmptyStateReason({ spaces, projects, canManage }: GetEmptyReasonParams): EmptyStateReason {
  // Owner/Admin never see empty state
  if (canManage) {
    return null;
  }

  // Filter to active projects only (Option B)
  const activeProjects = projects.filter(p => p.status === 'active');

  // Has active projects -> no empty state
  if (activeProjects.length > 0) {
    return null;
  }

  // No active projects
  if (spaces.length > 0) {
    // Has spaces but no active projects visible
    return 'spaces-but-no-projects';
  }

  // No content at all
  return 'no-content';
}
